import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AdminUser } from '../../types/admin';

interface AuthState {
  user: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// JWTトークンの有効期限チェック
const isTokenValid = (token: string): boolean => {
  try {
    // JWTトークンのペイロード部分をデコード
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    const decoded = JSON.parse(jsonPayload);
    const currentTime = Date.now() / 1000;
    
    // 有効期限をチェック（5分のマージンを持たせる）
    return decoded.exp && decoded.exp > currentTime + 300;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

// LocalStorageから認証情報を読み込む
const loadAuthFromStorage = (): { user: AdminUser | null; token: string | null; isAuthenticated: boolean } => {
  try {
    const token = localStorage.getItem('admin_token');
    const userString = localStorage.getItem('admin_user');
    
    if (token && userString) {
      // トークンの有効期限をチェック
      if (!isTokenValid(token)) {
        console.log('Token expired, clearing auth data');
        clearAuthFromStorage();
        return {
          user: null,
          token: null,
          isAuthenticated: false,
        };
      }
      
      const user = JSON.parse(userString) as AdminUser;
      return {
        user,
        token,
        isAuthenticated: true,
      };
    }
  } catch (error) {
    console.error('Failed to load auth from storage:', error);
    // LocalStorageの内容をクリア
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  }
  
  return {
    user: null,
    token: null,
    isAuthenticated: false,
  };
};

// LocalStorageに認証情報を保存
const saveAuthToStorage = (user: AdminUser, token: string) => {
  try {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(user));
  } catch (error) {
    console.error('Failed to save auth to storage:', error);
  }
};

// LocalStorageから認証情報を削除
const clearAuthFromStorage = () => {
  try {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  } catch (error) {
    console.error('Failed to clear auth from storage:', error);
  }
};

const authFromStorage = loadAuthFromStorage();

const initialState: AuthState = {
  user: authFromStorage.user || null,
  token: authFromStorage.token || null,
  isAuthenticated: authFromStorage.isAuthenticated || false,
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: AdminUser; token: string }>) => {
      state.isLoading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;
      
      // LocalStorageに保存
      saveAuthToStorage(action.payload.user, action.payload.token);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = action.payload;
      
      // LocalStorageからクリア
      clearAuthFromStorage();
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      
      // LocalStorageからクリア
      clearAuthFromStorage();
    },
    clearError: (state) => {
      state.error = null;
    },
    // 認証状態の復元（アプリ起動時に使用）
    restoreAuth: (state) => {
      const authData = loadAuthFromStorage();
      state.user = authData.user || null;
      state.token = authData.token || null;
      state.isAuthenticated = authData.isAuthenticated || false;
    },
  },
});

export const { loginStart, loginSuccess, loginFailure, logout, clearError, restoreAuth } = authSlice.actions;
export default authSlice.reducer; 